import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, CircleDot, Copy, Gem, Loader, Users } from 'lucide-react'
import { toast } from 'react-hot-toast'
import useDoubleStore from '../store/useDoubleStore'
import DoubleWheel from '../components/DoubleWheel'
import DoubleBetsFeed from '../components/DoubleBetsFeed'
import { hapticNotify } from '../../../utils/haptic'

const SHORT_ROUND_ID_LENGTH = 10
const COLOR_MULTIPLIER = { red: 2, black: 2, green: 14 }

const DoubleRound = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { t } = useTranslation()
  const { loadRoundById } = useDoubleStore()

  const [round, setRound] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadRound = async () => {
      setIsLoading(true)

      try {
        const data = await loadRoundById(id)
        setRound(data)
      } catch (error) {
        hapticNotify('error')
        toast.error(t('double.round.loadError', 'Не удалось загрузить раунд'))
      } finally {
        setIsLoading(false)
      }
    }

    loadRound()
  }, [id, loadRoundById, t])

  if (isLoading) {
    return (
      <div className="px-4 py-6 pb-16 lg:px-6 lg:py-2 lg:pb-8">
        <div className="mx-auto flex min-h-[60vh] max-w-7xl items-center justify-center">
          <Loader className="w-8 h-8 text-accent-primary animate-spin" />
        </div>
      </div>
    )
  }

  if (!round) {
    return (
      <div className="px-4 py-6 pb-16 lg:px-6 lg:py-2 lg:pb-8">
        <div className="mx-auto max-w-7xl rounded-2xl border border-red-400/30 bg-red-500/10 p-4 text-red-300">
          {t('double.round.empty', 'Раунд не найден')}
        </div>
      </div>
    )
  }

  const bets = Array.isArray(round.bets) ? round.bets : Array.isArray(round.users) ? round.users : []
  const roundStatus = round.status || 'finished'
  const sums = {
    red: Number(round.sum_red || 0),
    black: Number(round.sum_black || 0),
    green: Number(round.sum_green || 0)
  }
  const totalBank = sums.red + sums.black + sums.green
  const participantsCount = new Set(
    bets.map((bet, index) => bet?.chat_id || `${bet?.name || 'player'}-${index}`)
  ).size
  const myBets = bets.filter((bet) => bet?.your_bet)
  const myTotalSum = myBets.reduce((acc, bet) => acc + Number(bet?.sum || 0), 0)
  const myColorSums = myBets.reduce(
    (acc, bet) => {
      const colorKey = String(bet?.color || '').toLowerCase()
      if (!['red', 'black', 'green'].includes(colorKey)) return acc
      acc[colorKey] += Number(bet?.sum || 0)
      return acc
    },
    { red: 0, black: 0, green: 0 }
  )

  const fullRoundId = String(round.id || '')
  const shortRoundId = fullRoundId.length > SHORT_ROUND_ID_LENGTH
    ? `${fullRoundId.slice(0, SHORT_ROUND_ID_LENGTH)}…`
    : fullRoundId

  const normalizedWinColor = String(round.win_color || '').toLowerCase()
  const winSectorValue = String(round.win_sector ?? '—')
  const winMultiplier = COLOR_MULTIPLIER[normalizedWinColor] || 0
  const myWinningStake = Number(myColorSums[normalizedWinColor] || 0)
  const myPayout = myWinningStake * winMultiplier
  const myNetResult = myPayout - myTotalSum
  const myNetResultSign = myNetResult > 0 ? '+' : myNetResult < 0 ? '-' : ''

  const myResultToneClass = myNetResult > 0
    ? 'text-emerald-300'
    : myNetResult < 0
      ? 'text-rose-300'
      : 'text-white'

  const myResultLabelClass = myNetResult > 0
    ? 'border border-emerald-300/35 bg-emerald-500/15 text-emerald-100'
    : myNetResult < 0
      ? 'border border-rose-300/35 bg-rose-500/15 text-rose-100'
      : 'border border-white/20 bg-white/5 text-white'

  const myResultLabel = myNetResult > 0
    ? t('double.history.youWon', 'Выигрыш')
    : myNetResult < 0
      ? t('double.history.youLost', 'Проигрыш')
      : t('double.history.draw', 'Без изменений')

  const resultCircleClass = normalizedWinColor === 'red'
    ? 'bg-red-500 text-red-50 border-red-300/70'
    : normalizedWinColor === 'black'
      ? 'bg-slate-900 text-slate-100 border-slate-300/60'
      : 'bg-emerald-500 text-emerald-50 border-emerald-300/70'

  const handleCopyRoundId = async () => {
    if (!fullRoundId) return

    try {
      await navigator.clipboard.writeText(fullRoundId)
      hapticNotify('success')
      toast.success(t('double.round.idCopied', 'ID раунда скопирован'))
    } catch (error) {
      hapticNotify('error')
      toast.error(t('double.round.copyFailed', 'Не удалось скопировать ID'))
    }
  }

  return (
    <div className="px-4 py-6 pb-16 lg:px-6 lg:py-2 lg:pb-8 overflow-x-hidden">
      <div className="mx-auto max-w-7xl space-y-6 lg:space-y-8">
        <div className="relative flex items-center justify-center">
          <button
            onClick={() => navigate('/double/history')}
            className="absolute left-0 inline-flex items-center justify-center rounded-xl border border-white/10 bg-surface p-2.5 text-white hover:text-accent-primary"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <h1 className="flex items-center gap-2 text-2xl lg:text-4xl font-bold text-accent-primary">
            <CircleDot className="w-6 h-6 shrink-0 lg:w-8 lg:h-8" />
            <span>{t('double.round.title', 'Раунд Double')}</span>
          </h1>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-6">
            <DoubleWheel
              roundId={round.id}
              status={roundStatus}
              prevSector={round.prev_round?.win_sector}
              winSector={round.win_sector}
              winColor={round.win_color}
              countdownMs={0}
              raffleSeconds={round.second_to_finish_raffle}
              pinResultToCenter
              showResultSectorOnly
            />

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className={`rounded-2xl border px-4 py-3 ${round.win_color === 'red' ? 'border-red-300/70 bg-red-500/20' : 'border-red-300/35 bg-red-500/10'}`}>
                <p className="text-red-100 text-sm font-medium">{t('double.colors.red', 'Красный')}</p>
                <p className="mt-1 inline-flex items-center gap-1 text-red-50 font-semibold">
                  {sums.red.toLocaleString()} <Gem className="w-3.5 h-3.5" />
                </p>
              </div>

              <div className={`rounded-2xl border px-4 py-3 ${round.win_color === 'black' ? 'border-slate-200/60 bg-slate-700/45' : 'border-slate-300/35 bg-slate-900/45'}`}>
                <p className="text-slate-100 text-sm font-medium">{t('double.colors.black', 'Чёрный')}</p>
                <p className="mt-1 inline-flex items-center gap-1 text-slate-100 font-semibold">
                  {sums.black.toLocaleString()} <Gem className="w-3.5 h-3.5" />
                </p>
              </div>

              <div className={`rounded-2xl border px-4 py-3 ${round.win_color === 'green' ? 'border-emerald-300/70 bg-emerald-500/20' : 'border-emerald-300/35 bg-emerald-500/10'}`}>
                <p className="text-emerald-100 text-sm font-medium">{t('double.colors.green', 'Зелёный')}</p>
                <p className="mt-1 inline-flex items-center gap-1 text-emerald-50 font-semibold">
                  {sums.green.toLocaleString()} <Gem className="w-3.5 h-3.5" />
                </p>
              </div>
            </div>

            <DoubleBetsFeed
              bets={bets}
              roundStatus={roundStatus}
              winColor={round.win_color}
              enableResultAmountAnimation={false}
            />
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-white/10 bg-surface/50 p-4 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="inline-flex items-center gap-2 min-w-0">
                  <p className="text-xs text-white/90 font-mono truncate">{shortRoundId}</p>
                  <button
                    type="button"
                    onClick={handleCopyRoundId}
                    className="inline-flex items-center justify-center rounded-md border border-white/15 bg-dark-primary/40 p-1.5 text-white/80 hover:text-white"
                    aria-label={t('double.round.copyId', 'Скопировать ID раунда')}
                    title={t('double.round.copyId', 'Скопировать ID раунда')}
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="rounded-xl border border-white/10 bg-dark-primary/30 p-3 flex items-center justify-between gap-3">
                <p className="text-xs text-text-secondary">{t('double.round.result', 'Результат')}</p>
                <div className="flex items-center gap-2">
                  <div className={`w-16 h-16 rounded-full border-2 flex items-center justify-center text-base font-bold text-center px-1 ${resultCircleClass}`}>
                    {winSectorValue}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-xl border border-white/10 bg-dark-primary/25 p-3">
                  <p className="text-xs text-text-secondary">{t('double.history.totalBank', 'Банк')}</p>
                  <p className="mt-1 inline-flex items-center gap-1 text-white font-semibold">
                    {totalBank.toLocaleString()} <Gem className="w-3.5 h-3.5 text-accent-primary" />
                  </p>
                </div>

                <div className="rounded-xl border border-white/10 bg-dark-primary/25 p-3">
                  <p className="text-xs text-text-secondary">{t('double.round.players', 'Участники')}</p>
                  <p className="mt-1 inline-flex items-center gap-1 text-white font-semibold">
                    {participantsCount} <Users className="w-3.5 h-3.5 text-text-secondary" />
                  </p>
                </div>
              </div>

              {myBets.length > 0 && (
                <div className="space-y-3">
                  <div className="rounded-xl border border-white/10 bg-dark-primary/25 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-text-secondary">{t('double.round.myBet', 'Ваша ставка')}</p>
                      <p className="inline-flex items-center gap-1 text-white font-semibold">
                        {myTotalSum.toLocaleString()} <Gem className="w-3.5 h-3.5 text-accent-primary" />
                      </p>
                    </div>

                    <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                      <div className="rounded-lg border border-red-400/30 bg-red-500/10 px-2 py-1 text-red-100">
                        {myColorSums.red.toLocaleString()}
                      </div>
                      <div className="rounded-lg border border-slate-300/30 bg-slate-800/35 px-2 py-1 text-slate-100">
                        {myColorSums.black.toLocaleString()}
                      </div>
                      <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-2 py-1 text-emerald-100">
                        {myColorSums.green.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-white/10 bg-dark-primary/25 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-text-secondary">{t('double.round.myResult', 'Ваш результат')}</p>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${myResultLabelClass}`}>
                        {myResultLabel}
                      </span>
                    </div>

                    <p className={`mt-1 inline-flex items-center gap-1 text-lg font-bold ${myResultToneClass}`}>
                      {myNetResultSign}{Math.abs(myNetResult).toLocaleString()} <Gem className="w-4 h-4" />
                    </p>

                    <div className="mt-1 text-xs text-text-secondary">
                      {t('double.round.payout', 'Выплата')}: {myPayout.toLocaleString()} <Gem className="inline w-3 h-3 text-accent-primary" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DoubleRound
